import React from 'react'
import {
  Box,
  Typography,
  Alert,
  Grid,
  Card,
  CardContent,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  CircularProgress,
  Tabs,
  Tab,
  Paper,
  Divider,
  IconButton,
  Collapse,
  Badge,
  Tooltip,
} from '@mui/material'
import {
  Add as AddIcon,
  Edit as EditIcon,
  People as PeopleIcon,
  ArrowBack as ArrowBackIcon,
  Email as EmailIcon,
  CreditCard as CreditCardIcon,
  Business as BusinessIcon,
  TrendingUp as TrendingUpIcon,
  Settings as SettingsIcon,
  MeetingRoom as MeetingRoomIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Star as StarIcon,
  CheckCircle as CheckCircleIcon,
  Warning as WarningIcon,
  Info as InfoIcon,
  Shield as ShieldIcon,
} from '@mui/icons-material'
import { Company } from '../../../../core/models/Company'
import { useRoutingStore } from '../../../../stores/routingStore'
import { CompanyWithStats, normalizeBillingTier, getBillingTierColor } from '../../types'
import { useCompanyManagement } from '../../hooks/useCompanyManagement'
import { InviteUserDialog } from '../dialogs/InviteUserDialog'
import { CompanyDialog } from '../dialogs/CompanyDialog'
import { EditUserDialog } from '../dialogs/EditUserDialog'
import { UsersDialog } from '../dialogs/UsersDialog'
import { CompanyRoomsDialog } from '../dialogs/CompanyRoomsDialog'
import { CompanyService } from '../../../../services/CompanyService'
import { SEVV_SCROLLABLE_TABS_PROPS } from '../../../../theme/sevvScrollableTabsProps'
import { SEVV_TABS_OVERFLOW_SX } from '../../../../theme/sevvTabsOverflowSx'
import CompanyEmailTemplates from '../../../EmailTemplateManager/CompanyEmailTemplates'
import { LazyBillingManagementPanel } from '../billing/LazyBillingComponents'
import { WalletManagementPanel } from '../wallet/WalletManagementPanel'
import { billingService, BillingTier, CompanyBillingAccount, BillingCalculation, PaymentMethod, BillingHistory } from '../../../../services/BillingService'
import { pickStripePublishableConfig } from '../../../../services/BillingUtils'
import { StyledButton, StyledIconButton } from '../../styles/StyledComponents'
import { StripeProvider } from '../../../../contexts/StripeContext'
import { alpha } from '@mui/material/styles'
import type { SxProps, Theme } from '@mui/material/styles'
import { CompanyBrandedShell } from '../CompanyBrandedShell'
import Parse from 'parse'
import { useLiveQueryV2 } from '../../../Chat/hooks/useLiveQueryV2'
import debounce from 'lodash/debounce'
import { RolesPermissionsTab } from '../access/RolesPermissionsTab'
import { useSurface } from '../../../../hooks/usePermission'
import { companyDefaultRole, companyDefaultRoleKeyOf, memberHasNoRole } from '../../utils/roleOptions'
interface CompanyManagerViewProps {
  onBack?: () => void
  isCompanyManagerOrAdmin: boolean
  currentCompany: Company
}

// Tab Panel Component
interface TabPanelProps {
  children?: React.ReactNode
  index: number
  value: number
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props
  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`company-manager-tabpanel-${index}`}
      aria-labelledby={`company-manager-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 2.5 }}>{children}</Box>}
    </div>
  )
}

function a11yProps(index: number) {
  return {
    id: `company-manager-tab-${index}`,
    'aria-controls': `company-manager-tabpanel-${index}`,
  }
}

export const CompanyManagerView: React.FC<CompanyManagerViewProps & { initialCompanyTab?: 'overview' | 'billing' | 'users' | 'settings' | 'wallet' | 'setup' }> = ({
  onBack,
  isCompanyManagerOrAdmin,
  currentCompany,
  initialCompanyTab,
}) => {
  const routingStore = useRoutingStore()
  // What each part needs comes from the person's permissions in this company (SC-439), through the
  // shared surface ids; the server checks every action again. Overview is open to every member.
  const canViewBilling = useSurface('manager-view.billing-tab')
  const canViewUsers = useSurface('manager-view.users-tab')
  const canManageSettings = useSurface('manager-view.settings-tab')
  const canManageWallet = useSurface('manager-view.wallet-tab')
  const canViewRoles = useSurface('roles-permissions.tab')
  const canManageEmail = useSurface('manager-view.email-templates-tab')
  const canManageRooms = useSurface('rooms-page.manage-rooms')
  const canInvite = useSurface('manager-view.invite-button')
  const [showUsersDialog, setShowUsersDialog] = React.useState(false)
  // SC-422: company admins manage their own company's rooms.
  const [showRoomsDialog, setShowRoomsDialog] = React.useState(false)
  const [syncingUsers, setSyncingUsers] = React.useState(false)
  const [companyData, setCompanyData] = React.useState(currentCompany)
  const [emailTemplateDialogOpen, setEmailTemplateDialogOpen] = React.useState(false)
  const [billingTiers, setBillingTiers] = React.useState<BillingTier[]>([])
  const [loadingTiers, setLoadingTiers] = React.useState(false)
  const [activeTab, setActiveTab] = React.useState(0)
  // A tab the person may not open falls back to Overview, and returns when the permission
  // answer arrives, so a deep link to /company/billing works once it is known to be allowed.
  const tabAllowed: Record<number, boolean> = {
    0: true,
    1: canViewBilling,
    2: canViewUsers,
    3: canManageSettings,
    4: canManageWallet,
    5: canViewRoles,
  }
  const shownTab = tabAllowed[activeTab] ? activeTab : 0
  const [expandedSections, setExpandedSections] = React.useState<{ [key: string]: boolean }>({
    overview: true,
    billing: true,
    users: true,
  })
  
  // Billing management state
  const [billingAccount, setBillingAccount] = React.useState<CompanyBillingAccount | null>(null)
  const [billingCalculation, setBillingCalculation] = React.useState<BillingCalculation | null>(null)
  const [paymentMethods, setPaymentMethods] = React.useState<PaymentMethod[]>([])
  const [billingHistory, setBillingHistory] = React.useState<BillingHistory[]>([])
  const [stripeConfig, setStripeConfig] = React.useState<any>(null)
  /** Set only after a comprehensive snapshot response (success or failure) so the billing panel does not enter standalone multi-fetch mode. */
  const [subscriptions, setSubscriptions] = React.useState<any[] | undefined>(undefined)
  const [upcomingInvoice, setUpcomingInvoice] = React.useState<any>(null)
  const [billingError, setBillingError] = React.useState<string | null>(null)
  const [billingLoading, setBillingLoading] = React.useState(false)
  /** True after a successful comprehensive fetch (used for headcount alignment with billing_calculation). */
  const [billingDataLoaded, setBillingDataLoaded] = React.useState(false)
  const [overviewBillingAttemptDone, setOverviewBillingAttemptDone] = React.useState(false)
  const [fullBillingAttemptDone, setFullBillingAttemptDone] = React.useState(false)

  // Refresh company data
  const refreshCompanyData = React.useCallback(async () => {
    try {
      const result = await CompanyService.getCompany(currentCompany.id)
      if (result.success && result.company) {
        setCompanyData(result.company)
      }
    } catch (error) {
      console.error('Error refreshing company data:', error)
    }
  }, [currentCompany.id])

  // Load billing tiers
  const loadBillingTiers = React.useCallback(async () => {
    try {
      setLoadingTiers(true)
      const result = await billingService.getBillingTiers(true) // Get active tiers only
      if (result.success) {
        setBillingTiers(result.billing_tiers)
      }
    } catch (error) {
      console.error('Error loading billing tiers:', error)
    } finally {
      setLoadingTiers(false)
    }
  }, [])

  // Load billing tiers on component mount
  React.useEffect(() => {
    loadBillingTiers()
  }, [loadBillingTiers])

  React.useEffect(() => {
    setOverviewBillingAttemptDone(false)
    setFullBillingAttemptDone(false)
    setBillingDataLoaded(false)
    setBillingError(null)
    setBillingAccount(null)
    setBillingCalculation(null)
    setPaymentMethods([])
    setBillingHistory([])
    setStripeConfig(null)
    setSubscriptions(undefined)
    setUpcomingInvoice(null)
  }, [currentCompany.id])

  /**
   * Overview uses a light comprehensive request (no invoice history / upcoming) to limit Stripe API work.
   * Billing tab loads the full snapshot once; refresh forces the mode for the current tab.
   */
  const loadBillingData = React.useCallback(async (force: boolean, mode: 'light' | 'full') => {
    // The billing calls need billing.view_plan; without it the Overview shows the roster alone.
    if (!canViewBilling) return
    if (!force) {
      if (mode === 'light' && overviewBillingAttemptDone) return
      if (mode === 'full' && fullBillingAttemptDone) return
    }

    try {
      setBillingLoading(true)
      if (force) {
        setBillingError(null)
      } else if (mode === 'light') {
        setBillingError(null)
      }

      const opts =
        mode === 'light'
          ? { include_history: false, history_limit: 5, include_upcoming: false as const, include_payment_methods: false, include_subscriptions: false }
          : { include_history: true, history_limit: 5, include_upcoming: true as const, include_payment_methods: true, include_subscriptions: true }

      const result = await billingService.getComprehensiveBillingData(currentCompany.id, opts)

      if (result.success) {
        if (result.billing_account) {
          setBillingAccount(result.billing_account)
        }
        if (result.billing_calculation) {
          setBillingCalculation(result.billing_calculation)
        }
        if (result.payment_methods) {
          setPaymentMethods(result.payment_methods)
        }
        if (result.billing_history) {
          setBillingHistory(result.billing_history)
        } else if (mode === 'full') {
          setBillingHistory([])
        }

        if (result.stripe_config) {
          setStripeConfig(result.stripe_config)
        }

        setSubscriptions(Array.isArray(result.subscriptions) ? result.subscriptions : [])

        if (result.upcoming_invoice) {
          setUpcomingInvoice(result.upcoming_invoice)
        } else if (mode === 'full') {
          setUpcomingInvoice(null)
        }

        setBillingDataLoaded(true)
      } else {
        if (import.meta.env.DEV) {
          console.error('Failed to load comprehensive billing data:', result.error)
        }
        setBillingError(result.error || 'Failed to load billing information. Please try again.')
        setSubscriptions([])
        setBillingDataLoaded(false)
      }
    } catch (error) {
      if (import.meta.env.DEV) {
        console.error('Error loading billing data:', error)
      }
      setBillingError('Failed to load billing information. Please try again.')
      setSubscriptions([])
      setBillingDataLoaded(false)
    } finally {
      setBillingLoading(false)
      if (mode === 'light') {
        setOverviewBillingAttemptDone(true)
      } else {
        setFullBillingAttemptDone(true)
        setOverviewBillingAttemptDone(true)
      }
    }
  }, [currentCompany.id, overviewBillingAttemptDone, fullBillingAttemptDone, canViewBilling])

  React.useEffect(() => {
    if (shownTab === 0) {
      void loadBillingData(false, 'light')
    } else if (shownTab === 1) {
      void loadBillingData(false, 'full')
    }
  }, [shownTab, loadBillingData])

  const refreshBillingForActiveTab = React.useCallback(async () => {
    setOverviewBillingAttemptDone(false)
    setFullBillingAttemptDone(false)
    setBillingDataLoaded(false)
    await loadBillingData(true, shownTab === 1 ? 'full' : 'light')
  }, [shownTab, loadBillingData])

  const {
    companyUsers,
    totalCompanyUsers,
    loading,
    dialogs,
    companyForm,
    inviteForm,
    editUserForm,
    userMenu,
    selectedItems,
    setCompanyForm,
    setInviteForm,
    setEditUserForm,
    handleInviteUser,
    handleEditCompany,
    handleEditCompanyOpen,
    handleEditUser,
    handleEditUserOpen,
    handleRemoveUser,
    handleUserMenuOpen,
    handleUserMenuClose,
    openDialog,
    closeDialog,
    loadCompanyUsers,
  } = useCompanyManagement(false, isCompanyManagerOrAdmin, false, currentCompany)

  const handleCompanyDialogSubmit = React.useCallback(async () => {
    const out = await handleEditCompany()
    if (out.success && out.company) {
      setCompanyData(out.company)
    }
  }, [handleEditCompany])

  // Load users on mount so stat cards on the Overview tab are immediately accurate.
  // A subsequent load when the Users tab is activated is de-duped by the loading flag.
  React.useEffect(() => {
    loadCompanyUsers(currentCompany.id)
  }, [currentCompany.id, loadCompanyUsers])

  React.useEffect(() => {
    setCompanyData(currentCompany)
  }, [currentCompany])

  const sessionToken = Parse.User.current()?.getSessionToken()
  const { subscribe, unsubscribe } = useLiveQueryV2(sessionToken)

  const debouncedRefreshCompanyData = React.useMemo(() => debounce(refreshCompanyData, 1500), [refreshCompanyData])
  const debouncedLoadCompanyUsers = React.useMemo(() => debounce(() => loadCompanyUsers(currentCompany.id), 1500), [loadCompanyUsers, currentCompany.id])
  const debouncedRefreshBilling = React.useMemo(() => debounce(refreshBillingForActiveTab, 1500), [refreshBillingForActiveTab])

  React.useEffect(() => {
    if (!currentCompany?.id || !sessionToken) return

    const companyQuery = new Parse.Query('Company')
    companyQuery.equalTo('objectId', currentCompany.id)
    const companySubKey = `manager-company-${currentCompany.id}`

    const userQuery = new Parse.Query('CompanyUser')
    userQuery.equalTo('company_id', currentCompany.id)
    const userSubKey = `manager-users-${currentCompany.id}`

    const billingQuery = new Parse.Query('CompanyBillingAccount')
    billingQuery.equalTo('company_id', currentCompany.id)
    const billingSubKey = `manager-billing-${currentCompany.id}`

    subscribe(companyQuery, (obj, event) => {
      if (event === 'update') debouncedRefreshCompanyData()
    }, companySubKey)

    subscribe(userQuery, (obj, event) => {
      if (event === 'create' || event === 'update' || event === 'delete') debouncedLoadCompanyUsers()
    }, userSubKey)

    subscribe(billingQuery, (obj, event) => {
      if (event === 'update') debouncedRefreshBilling()
    }, billingSubKey)

    return () => {
      unsubscribe(companySubKey)
      unsubscribe(userSubKey)
      unsubscribe(billingSubKey)
      debouncedRefreshCompanyData.cancel()
      debouncedLoadCompanyUsers.cancel()
      debouncedRefreshBilling.cancel()
    }
  }, [currentCompany.id, sessionToken, subscribe, unsubscribe, debouncedRefreshCompanyData, debouncedLoadCompanyUsers, debouncedRefreshBilling])

  const handleEditCompanyLocal = () => {
    handleEditCompanyOpen(companyData as CompanyWithStats)
  }

  const handleViewUsers = () => {
    loadCompanyUsers(currentCompany.id)
    setShowUsersDialog(true)
  }

  const handleCloseUsersDialog = () => {
    setShowUsersDialog(false)
  }

  const handleManageEmailTemplates = () => {
    setEmailTemplateDialogOpen(true)
  }

  const handleCloseEmailTemplateDialog = () => {
    setEmailTemplateDialogOpen(false)
  }


  const handleInviteUserFromUsersDialog = () => {
    setShowUsersDialog(false)
    openDialog('showInviteDialog')
  }

  // Override the handleInviteUser to include refresh and reopen UsersDialog
  const handleInviteUserWithRefresh = async () => {
    await handleInviteUser()
    // Refresh users after inviting user and reopen UsersDialog to show updated list
    setTimeout(() => {
      loadCompanyUsers(currentCompany.id)
      // Reopen UsersDialog to show the updated user list (including pending users)
      setShowUsersDialog(true)
    }, 200) // Much faster - 200ms instead of 1000ms
  }

  const handleEditUserFromMenu = () => {
    handleEditUserOpen(userMenu.selectedUserForMenu!)
  }

  // Override the handleEditUser to include refresh
  const handleEditUserWithRefresh = async () => {
    await handleEditUser()
    // Refresh users after editing user (company data doesn't need refresh)
    setTimeout(() => {
      loadCompanyUsers(currentCompany.id)
    }, 200) // Faster refresh
  }

  const handleRemoveUserFromMenu = () => {
    handleRemoveUser()
    // Refresh users after user removal (company data doesn't need refresh)
    setTimeout(() => {
      loadCompanyUsers(currentCompany.id)
    }, 200) // Faster refresh
  }

  const handleSyncUnassignedUsers = async () => {
    try {
      setSyncingUsers(true)
      const result = await CompanyService.syncUnassignedUsers(currentCompany.id)
      
      if (result.success) {
        // Reload users after sync (company data doesn't need refresh)
        loadCompanyUsers(currentCompany.id)
      }
    } catch (error) {
      console.error('Error syncing unassigned users:', error)
    } finally {
      setSyncingUsers(false)
    }
  }

  // Tab handling
  const handleTabChange = (event: React.SyntheticEvent, newValue: number) => {
    setActiveTab(newValue)
    // Sync URL with selected tab
    switch (newValue) {
      case 0:
        routingStore.navigateTo('/company/overview')
        break
      case 1:
        routingStore.navigateTo('/company/billing')
        break
      case 2:
        routingStore.navigateTo('/company/users')
        break
      case 3:
        routingStore.navigateTo('/company/setup')
        break
      case 4:
        routingStore.navigateTo('/company/wallet')
        break
      default:
        // Roles & permissions has no page of its own: the URL stays where it was.
        break
    }
  }

  // On mount and when route changes, sync selected tab from URL
  React.useEffect(() => {
    const path = routingStore.currentPath
    if (path.startsWith('/company/')) {
      if (path.includes('/overview') && activeTab !== 0) setActiveTab(0)
      else if (path.includes('/billing') && activeTab !== 1) setActiveTab(1)
      else if (path.includes('/users') && activeTab !== 2) setActiveTab(2)
      else if ((path.includes('/settings') || path.includes('/setup')) && activeTab !== 3) setActiveTab(3)
      else if (path.includes('/wallet') && activeTab !== 4) setActiveTab(4)
    }
  }, [routingStore.currentPath])

  // Respect initial tab if provided (first render only)
  React.useEffect(() => {
    if (!initialCompanyTab) return
    switch (initialCompanyTab) {
      case 'overview': setActiveTab(0); break
      case 'billing': setActiveTab(1); break
      case 'users': setActiveTab(2); break
      case 'settings': setActiveTab(3); break
      case 'setup': setActiveTab(3); break // Setup maps to settings tab
      case 'wallet': setActiveTab(4); break
      default: break
    }
  }, [])

  // Section expansion handling
  const toggleSection = (sectionKey: string) => {
    setExpandedSections(prev => ({
      ...prev,
      [sectionKey]: !prev[sectionKey]
    }))
  }

  /**
   * Active members: align with Billing Status (server CompanyUser count) when comprehensive billing
   * has loaded; otherwise roster from cloud. Math.max avoids a capped cloud page (e.g. 100) undercounting.
   */
  const fromRoster = totalCompanyUsers ?? companyUsers.length
  const fromBilling =
    billingDataLoaded && billingCalculation != null && typeof billingCalculation.active_users === 'number'
      ? billingCalculation.active_users
      : null
  const activeMemberCount = fromBilling != null ? Math.max(fromRoster, fromBilling) : fromRoster

  // Smart tier analysis
  const getTierRecommendations = () => {
    // Use billing account tier data if available, otherwise fallback to billingTiers
    const currentTier = billingAccount?.billing_tier || billingTiers.find(tier => tier.tier_key === companyData.billing_tier)
    const userCount = activeMemberCount
    const activeUserCount = activeMemberCount
    
    if (!currentTier) return { recommendations: [], upgrades: [], downgrades: [] }

    const recommendations = []
    const upgrades = []
    const downgrades = []

    // Approaching included seats (not yet over): prompt upgrade before hitting the cap.
    // When already over included seats, a dedicated severity alert explains capacity and add blocking — not this banner.
    if (
      currentTier.max_users !== -1 &&
      userCount > currentTier.max_users * 0.8 &&
      userCount <= currentTier.max_users
    ) {
      const upgradeTier = billingTiers.find(tier =>
        tier.max_users > currentTier.max_users && tier.is_active
      )
      if (upgradeTier) {
        upgrades.push({
          tier: upgradeTier,
          reason: `You are using ${userCount} of ${currentTier.max_users} included seats. Consider upgrading before you reach your plan limit.`,
          priority: 'high'
        })
      }
    }

    // Check if underutilizing current tier
    if (userCount < currentTier.free_users_limit * 0.5 && billingTiers.length > 1) {
      const downgradeTier = billingTiers.find(tier => 
        tier.max_users < currentTier.max_users && tier.is_active
      )
      if (downgradeTier) {
        downgrades.push({
          tier: downgradeTier,
          reason: `Underutilizing current tier (${userCount} users)`,
          priority: 'low'
        })
      }
    }

    // Check for cost optimization - use billing calculation if available
    const currentCost = billingCalculation?.total_cost || 
      (currentTier.base_price || 0) + (Math.max(0, activeUserCount - (currentTier.free_users_limit || 0)) * (currentTier.price_per_user || 0))
    
    const betterTier = billingTiers.find(tier => {
      if (tier.tier_key === currentTier.tier_key || !tier.is_active) return false
      const tierCost = (tier.base_price || 0) + (Math.max(0, activeUserCount - (tier.free_users_limit || 0)) * (tier.price_per_user || 0))
      return tierCost < currentCost * 0.9 // 10% savings
    })

    if (betterTier) {
      const betterTierCost = (betterTier.base_price || 0) + (Math.max(0, activeUserCount - (betterTier.free_users_limit || 0)) * (betterTier.price_per_user || 0))
      const savings = currentCost - betterTierCost
      recommendations.push({
        tier: betterTier,
        reason: `Potential cost savings of $${savings.toFixed(2)}/month`,
        priority: 'medium'
      })
    }

    return { recommendations, upgrades, downgrades }
  }

  const tierAnalysis = getTierRecommendations()

  // Get current billing tier max users from billing account data
  const getCurrentTierMaxUsers = () => {
    // First try to get from billing account (most accurate)
    if (billingAccount?.billing_tier?.max_users !== undefined) {
      return billingAccount.billing_tier.max_users === -1 ? 'Unlimited' : billingAccount.billing_tier.max_users
    }
    
    // Fallback to billingTiers state
    const currentTier = billingTiers.find(tier => tier.tier_key === companyData.billing_tier)
    if (currentTier) {
      return currentTier.max_users === -1 ? 'Unlimited' : currentTier.max_users
    }
    
    // Final fallback
    return companyData.max_users || 100
  }

  const planSeatsDisplay = getCurrentTierMaxUsers()
  const planSeatsNum = planSeatsDisplay === 'Unlimited' ? Infinity : Number(planSeatsDisplay)
  const showOverCapacityAlert =
    !loading.loadingUsers && planSeatsNum !== Infinity && activeMemberCount > planSeatsNum

  const overCapacitySeatsAlert = showOverCapacityAlert ? (
    <Alert severity="error" sx={{ mb: 2 }} icon={<WarningIcon />}>
      <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 1, color: 'inherit' }}>
        Plan capacity exceeded
      </Typography>
      <Typography variant="body2" sx={{ mb: 1.5 }}>
        This company currently has <strong>{activeMemberCount}</strong> active members, above the included seat allowance
        for your current plan (<strong>{planSeatsDisplay}</strong>). Ongoing billing will include additional charges for
        seats beyond that allowance, in line with your subscription terms.
      </Typography>
      <Typography variant="body2">
        <strong>New invitations and member additions are blocked</strong> until you upgrade to a plan with sufficient
        capacity for your roster. Open <strong>Manage Plan</strong> to upgrade, or contact your platform administrator if
        you do not have billing access.
      </Typography>
    </Alert>
  ) : null

  const cardStyle: SxProps<Theme> = {
    p: 2.5,
    height: '100%',
    minHeight: '280px',
    border: (t) => `1px solid ${t.palette.divider}`,
    borderRadius: '12px',
    bgcolor: 'background.paper',
    color: 'text.primary',
    backgroundImage: 'none',
    boxShadow: (t) => t.shadows[1],
    transition: 'all 0.3s ease',
    cursor: 'pointer',
    '&:hover': {
      borderColor: 'primary.main',
      bgcolor: 'background.paper',
      transform: 'translateY(-2px)',
      boxShadow: (t) => t.shadows[4],
    },
  }

  const sectionPaperSx: SxProps<Theme> = {
    p: 2.5,
    border: (t) => `1px solid ${t.palette.divider}`,
    borderRadius: 2.5,
    bgcolor: 'background.paper',
    color: 'text.primary',
    backgroundImage: 'none',
    backdropFilter: 'blur(10px)',
    transition: 'all 0.3s ease',
    '&:hover': {
      borderColor: (theme) => alpha(theme.palette.primary.main, 0.3),
      bgcolor: 'background.paper',
      transform: 'translateY(-2px)',
      boxShadow: (t) => t.shadows[8],
    },
  }

  return (
    <CompanyBrandedShell company={companyData}>
    <Box sx={{ width: '100%' }}>
      {/* Header */}
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          mb: 2.5,
          pb: 2,
          borderBottom: 1,
          borderColor: 'divider',
        }}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          {onBack && (
            <StyledIconButton
              onClick={onBack}
              sx={{
                border: (t) => `1px solid ${t.palette.divider}`,
                borderRadius: '8px',
                p: 0.75,
                '&:hover': {
                  borderColor: 'primary.main',
                  backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.1),
                },
              }}
            >
              <ArrowBackIcon sx={{ fontSize: 18 }} />
            </StyledIconButton>
          )}
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
            <Box
              sx={{
                p: 1,
                borderRadius: '8px',
                backgroundColor: 'var(--sevv-surface-tint)',
                border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
              }}
            >
              <BusinessIcon sx={{ fontSize: 20, color: 'primary.main' }} />
            </Box>
            <Box>
              <Typography variant="h5" sx={{ fontWeight: 600, color: 'text.primary', mb: 0.25, fontSize: '1.5rem' }}>
                Company Management
              </Typography>
              <Typography variant="body2" sx={{ color: 'text.secondary', fontSize: '0.875rem' }}>
                {companyData.name}
              </Typography>
            </Box>
          </Box>
        </Box>

        <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
          {canManageEmail && (
            <StyledButton
              variant="outlined"
              startIcon={<EmailIcon sx={{ fontSize: 16 }} />}
              onClick={handleManageEmailTemplates}
              size="small"
              sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
            >
              Email
            </StyledButton>
          )}
          {canManageRooms && (
            <StyledButton
              variant="outlined"
              startIcon={<MeetingRoomIcon sx={{ fontSize: 16 }} />}
              onClick={() => setShowRoomsDialog(true)}
              size="small"
              sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
            >
              Rooms
            </StyledButton>
          )}
          {canViewBilling && (
            <StyledButton
              variant="outlined"
              startIcon={<SettingsIcon sx={{ fontSize: 16 }} />}
              onClick={() => setActiveTab(1)}
              size="small"
              sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
            >
              Manage Plan
            </StyledButton>
          )}
          {canInvite && (
            <StyledButton
              variant="contained"
              startIcon={<AddIcon sx={{ fontSize: 16 }} />}
              onClick={() => openDialog('showInviteDialog')}
              size="small"
              sx={{ minWidth: 'auto', px: 1.5, py: 0.5 }}
            >
              Invite
            </StyledButton>
          )}
        </Box>
      </Box>

      {/* Smart Alerts */}
      {tierAnalysis.upgrades.length > 0 && (
        <Box
          sx={{
            p: 1.5,
            mb: 2,
            bgcolor: (t) => alpha(t.palette.warning.main, 0.12),
            border: (t) => `1px solid ${alpha(t.palette.warning.main, 0.35)}`,
            color: 'warning.main',
            borderRadius: 1.5,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 0.5 }}>
            <WarningIcon sx={{ color: 'warning.main', fontSize: 16 }} />
            <Typography variant="subtitle2" sx={{ color: 'warning.main', fontSize: '0.875rem' }}>
              Tier Upgrade Recommended
            </Typography>
          </Box>
          <Typography variant="body2" sx={{ color: 'warning.main', fontSize: '0.8rem' }}>
            {tierAnalysis.upgrades[0].reason}
          </Typography>
        </Box>
      )}

      {overCapacitySeatsAlert}

      {shownTab === 0 && billingError && overviewBillingAttemptDone && (
        <Alert severity="warning" sx={{ mb: 2 }} onClose={() => setBillingError(null)}>
          <Typography variant="body2">
            Billing snapshot could not be loaded. Seat counts use your member list only. Open Billing &amp; Tiers and use Refresh, or try again later.
          </Typography>
        </Alert>
      )}

      {/* Main Content with Tabs */}
      <Box
        sx={{
          borderRadius: 2.5,
          overflow: 'hidden',
          border: (t) => `1px solid ${t.palette.divider}`,
          bgcolor: 'background.paper',
          color: 'text.primary',
          backgroundImage: 'none',
          backdropFilter: 'blur(10px)',
          boxShadow: (t) => t.shadows[2],
        }}
      >
        {/* Tabs */}
        <Box sx={{ 
          borderBottom: 1, 
          borderColor: 'divider',
          bgcolor: 'action.hover',
        }}>
          <Tabs
            {...SEVV_SCROLLABLE_TABS_PROPS}
            value={shownTab}
            onChange={handleTabChange}
            aria-label="company management tabs"
            sx={{
              ...SEVV_TABS_OVERFLOW_SX,
              minHeight: 'auto',
              '& .MuiTab-root': {
                color: 'text.secondary',
                fontWeight: 500,
                textTransform: 'none',
                fontSize: '0.875rem',
                minHeight: 'auto',
                minWidth: 'auto',
                maxWidth: 'none',
                whiteSpace: 'nowrap',
                padding: '12px 16px',
                '&.Mui-selected': {
                  color: 'primary.main',
                  fontWeight: 600,
                },
                '&:hover': {
                  color: 'text.primary',
                },
              },
              '& .MuiTabs-indicator': {
                backgroundColor: 'primary.main',
                height: '2px',
              },
            }}
          >
            <Tab
              value={0}
              icon={<BusinessIcon sx={{ fontSize: 18 }} />}
              label="Overview"
              iconPosition="start"
              {...a11yProps(0)}
            />
            {canViewBilling && (
              <Tab
                value={1}
                icon={<TrendingUpIcon sx={{ fontSize: 18 }} />}
                label="Billing & Tiers"
                iconPosition="start"
                {...a11yProps(1)}
              />
            )}
            {canViewUsers && (
              <Tab
                value={2}
                icon={<PeopleIcon sx={{ fontSize: 18 }} />}
                label="Users"
                iconPosition="start"
                {...a11yProps(2)}
              />
            )}
            {canManageSettings && (
              <Tab
                value={3}
                icon={<SettingsIcon sx={{ fontSize: 18 }} />}
                label="Settings"
                iconPosition="start"
                {...a11yProps(3)}
              />
            )}
            {canManageWallet && (
              <Tab
                value={4}
                icon={<CreditCardIcon sx={{ fontSize: 18 }} />}
                label="Wallet"
                iconPosition="start"
                {...a11yProps(4)}
              />
            )}
            {canViewRoles && (
              <Tab
                value={5}
                icon={<ShieldIcon sx={{ fontSize: 18 }} />}
                label="Roles & permissions"
                iconPosition="start"
                {...a11yProps(5)}
              />
            )}
          </Tabs>
        </Box>

        {/* Tab Panels */}
        <Box sx={{ bgcolor: 'background.paper', color: 'text.primary', backgroundImage: 'none' }}>
          {/* Overview Tab */}
          <TabPanel value={shownTab} index={0}>
            <Box sx={{ 
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
              minHeight: '100%',
              p: 2,
              borderRadius: 2
            }}>
              {billingLoading && !overviewBillingAttemptDone ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '30vh' }}>
                  <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
              ) : (
                <Grid container spacing={3}>
                {/* Company Information Section */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={cardStyle}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1.5,
                          background: (theme) => alpha(theme.palette.primary.main, 0.1),
                          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          mr: 1.5,
                        }}
                      >
                        <BusinessIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ 
                        color: 'text.primary', 
                        fontWeight: 600, 
                        fontSize: '1rem',
                        fontFamily: 'Inter, Segoe UI, Roboto, Helvetica Neue, Arial, sans-serif',
                        letterSpacing: '-0.01em',
                        lineHeight: 1.25
                      }}>
                        Company Information
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                          Name
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, textAlign: 'right', flex: 1 }}>
                          {companyData.name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                          Domain
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, textAlign: 'right', flex: 1 }}>
                          {companyData.domain}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                          Platform
                        </Typography>
                        <Typography variant="body2" sx={{ color: 'text.primary', fontWeight: 500, textAlign: 'right', flex: 1 }}>
                          {companyData.platform_name}
                        </Typography>
                      </Box>
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                        <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                          Status
                        </Typography>
                        <Chip
                          label={companyData.status}
                          size="small"
                          sx={(t) => ({
                            bgcolor:
                              companyData.status === 'active'
                                ? alpha(t.palette.success.main, 0.14)
                                : t.palette.action.hover,
                            color:
                              companyData.status === 'active'
                                ? t.palette.success.main
                                : t.palette.text.secondary,
                            fontWeight: 500,
                            height: '24px',
                            fontSize: '0.75rem',
                          })}
                        />
                      </Box>
                    </Box>
                  </Paper>
                </Grid>

                {/* Company Statistics Section */}
                <Grid item xs={12} md={6}>
                  <Paper
                    elevation={0}
                    sx={cardStyle}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1.5,
                          background: (theme) => alpha(theme.palette.primary.main, 0.1),
                          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          mr: 1.5,
                        }}
                      >
                        <PeopleIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1rem' }}>
                        Company Statistics
                      </Typography>
                    </Box>
                    {(() => {
                      const maxUsers = getCurrentTierMaxUsers()
                      const total = activeMemberCount
                      const maxNum = maxUsers === 'Unlimited' ? Infinity : Number(maxUsers)
                      const overLimit = !loading.loadingUsers && maxNum !== Infinity && total > maxNum
                      const pct = maxNum === Infinity || loading.loadingUsers
                        ? null
                        : Math.round((total / maxNum) * 100)

                      return (
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
                          {/* Total Members */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                              Total Members
                            </Typography>
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, justifyContent: 'flex-end', flex: 1 }}>
                              {overLimit && (
                                <Tooltip title={`Over plan limit by ${total - maxNum} member${total - maxNum !== 1 ? 's' : ''}`}>
                                  <WarningIcon sx={{ fontSize: 16, color: 'error.main' }} />
                                </Tooltip>
                              )}
                              <Typography variant="h6" sx={{ color: overLimit ? 'error.main' : 'primary.main', fontWeight: 600, textAlign: 'right' }}>
                                {loading.loadingUsers ? '…' : total}
                              </Typography>
                            </Box>
                          </Box>

                          {/* Plan included seats */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                              Plan seats
                            </Typography>
                            <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, textAlign: 'right', flex: 1 }}>
                              {maxUsers}
                            </Typography>
                          </Box>

                          {/* Capacity Used */}
                          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', minHeight: '32px' }}>
                            <Typography variant="body2" sx={{ color: 'text.secondary', textAlign: 'left', minWidth: '100px' }}>
                              Capacity Used
                            </Typography>
                            <Typography
                              variant="h6"
                              sx={{
                                color: overLimit
                                  ? 'error.main'
                                  : pct !== null && pct >= 80
                                    ? 'warning.main'
                                    : 'info.main',
                                fontWeight: 600,
                                textAlign: 'right',
                                flex: 1,
                              }}
                            >
                              {pct === null ? '—' : `${pct}%`}
                            </Typography>
                          </Box>
                        </Box>
                      )
                    })()}
                  </Paper>
                </Grid>

                {/* Quick Actions Section */}
                <Grid item xs={12}>
                  <Paper
                    elevation={0}
                    sx={sectionPaperSx}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                      <Box
                        sx={{
                          p: 0.75,
                          borderRadius: 1.5,
                          background: (theme) => alpha(theme.palette.primary.main, 0.1),
                          border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                          mr: 1.5,
                        }}
                      >
                        <SettingsIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                      </Box>
                      <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1rem' }}>
                        Quick Actions
                      </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                      {canManageEmail && (
                        <StyledButton
                          variant="outlined"
                          startIcon={<EmailIcon />}
                          onClick={handleManageEmailTemplates}
                          size="small"
                        >
                          Email Templates
                        </StyledButton>
                      )}
                      {canViewBilling && (
                        <StyledButton
                          variant="outlined"
                          startIcon={<CreditCardIcon />}
                          onClick={() => setActiveTab(1)}
                          size="small"
                        >
                          Billing Management
                        </StyledButton>
                      )}
                      {canViewUsers && (
                        <StyledButton
                          variant="outlined"
                          startIcon={<PeopleIcon />}
                          onClick={handleViewUsers}
                          size="small"
                        >
                          Manage Users ({loading.loadingUsers ? '…' : activeMemberCount})
                        </StyledButton>
                      )}
                      
                    </Box>
                  </Paper>
                </Grid>
              </Grid>
              )}
            </Box>
          </TabPanel>

          {/* Billing & Tiers Tab — Stripe only mounts here so Email Templates / other tabs skip loadStripe */}
          <TabPanel value={shownTab} index={1}>
            <StripeProvider prefetchedStripeConfig={pickStripePublishableConfig(stripeConfig)}>
              <Box sx={{ 
                background: (theme) =>
                  `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
                minHeight: '100%',
                p: 0,
                borderRadius: 2,
                overflow: 'hidden'
              }}>
                {billingLoading && !fullBillingAttemptDone ? (
                  <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                    <CircularProgress sx={{ color: 'primary.main' }} />
                  </Box>
                ) : (
                  <LazyBillingManagementPanel
                    company={companyData}
                    onBack={undefined} // No back button since we're in a tab
                    billingAccount={billingAccount ?? undefined}
                    billingCalculation={billingCalculation ?? undefined}
                    paymentMethods={paymentMethods ?? undefined}
                    billingHistory={billingHistory ?? undefined}
                    stripeConfig={stripeConfig ?? undefined}
                    subscriptions={subscriptions}
                    upcomingInvoice={upcomingInvoice ?? undefined}
                    loading={billingLoading}
                    error={billingError ?? undefined}
                    onRefresh={refreshBillingForActiveTab}
                    parentOwnsBillingSnapshot
                  />
                )}
              </Box>
            </StripeProvider>
          </TabPanel>

          {/* Users Tab */}
          <TabPanel value={shownTab} index={2}>
            <Box sx={{ 
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
              minHeight: '100%',
              p: 2,
              borderRadius: 2
            }}>
              {loading.loadingUsers ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '50vh' }}>
                  <CircularProgress sx={{ color: 'primary.main' }} />
                </Box>
              ) : (
                <Paper
                elevation={0}
                sx={sectionPaperSx}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    <Box
                      sx={{
                        p: 0.75,
                        borderRadius: 1.5,
                        background: (theme) => alpha(theme.palette.primary.main, 0.1),
                        border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                        mr: 1.5,
                      }}
                    >
                      <PeopleIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                    </Box>
                    <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1rem' }}>
                      User Management
                    </Typography>
                  </Box>
                 
                </Box>
                
                {(() => {
                  const maxUsers = getCurrentTierMaxUsers()
                  const total = activeMemberCount
                  const maxNum = maxUsers === 'Unlimited' ? Infinity : Number(maxUsers)
                  const overLimit = maxNum !== Infinity && total > maxNum
                  const pct = maxNum === Infinity ? null : Math.round((total / maxNum) * 100)

                  return (
                    <>
                    <Grid container spacing={2}>
                      {/* Total Members */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Tooltip
                          title={overLimit ? `${total - maxNum} member${total - maxNum !== 1 ? 's' : ''} over plan limit` : ''}
                          disableHoverListener={!overLimit}
                        >
                          <Box
                            sx={(t) => ({
                              textAlign: 'center',
                              p: 2,
                              borderRadius: 1,
                              bgcolor: overLimit
                                ? alpha(t.palette.error.main, 0.08)
                                : alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.04),
                              border: overLimit
                                ? `1px solid ${alpha(t.palette.error.main, 0.35)}`
                                : '1px solid transparent',
                            })}
                          >
                            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 0.5 }}>
                              {overLimit && <WarningIcon sx={{ fontSize: 20, color: 'error.main' }} />}
                              <Typography variant="h4" sx={{ color: overLimit ? 'error.main' : 'primary.main', fontWeight: 600 }}>
                                {total}
                              </Typography>
                            </Box>
                            <Typography variant="body2" sx={{ color: overLimit ? 'error.main' : 'text.secondary', fontWeight: overLimit ? 600 : 400 }}>
                              Total Members
                            </Typography>
                            {overLimit && (
                              <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.25 }}>
                                {total - maxNum} over included seats
                              </Typography>
                            )}
                          </Box>
                        </Tooltip>
                      </Grid>

                      {/* Plan included seats */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Box
                          sx={(t) => ({
                            textAlign: 'center',
                            p: 2,
                            borderRadius: 1,
                            bgcolor: overLimit
                              ? alpha(t.palette.error.main, 0.08)
                              : alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.04),
                            border: overLimit
                              ? `1px solid ${alpha(t.palette.error.main, 0.35)}`
                              : '1px solid transparent',
                          })}
                        >
                          <Typography variant="h4" sx={{ color: overLimit ? 'error.main' : 'info.main', fontWeight: 600 }}>
                            {maxUsers}
                          </Typography>
                          <Typography variant="body2" sx={{ color: overLimit ? 'error.main' : 'text.secondary', fontWeight: overLimit ? 600 : 400 }}>
                            Plan seats
                          </Typography>
                          {overLimit && (
                            <Typography variant="caption" sx={{ color: 'error.main', display: 'block', mt: 0.25 }}>
                              Seat limit exceeded
                            </Typography>
                          )}
                        </Box>
                      </Grid>

                      {/* Capacity Used */}
                      <Grid item xs={12} sm={6} md={4}>
                        <Box
                          sx={(t) => ({
                            textAlign: 'center',
                            p: 2,
                            borderRadius: 1,
                            bgcolor: overLimit
                              ? alpha(t.palette.error.main, 0.08)
                              : alpha(t.palette.primary.main, t.palette.mode === 'dark' ? 0.08 : 0.04),
                            border: overLimit
                              ? `1px solid ${alpha(t.palette.error.main, 0.35)}`
                              : '1px solid transparent',
                          })}
                        >
                          <Typography
                            variant="h4"
                            sx={{
                              fontWeight: 600,
                              color: overLimit
                                ? 'error.main'
                                : pct !== null && pct >= 80
                                  ? 'warning.main'
                                  : 'secondary.main',
                            }}
                          >
                            {pct === null ? '∞' : `${pct}%`}
                          </Typography>
                          <Typography variant="body2" sx={{ color: overLimit ? 'error.main' : 'text.secondary', fontWeight: overLimit ? 600 : 400 }}>
                            Capacity Used
                          </Typography>
                        </Box>
                      </Grid>
                    </Grid>
                    </>
                  )
                })()}

                <Box sx={{ mt: 3, display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <StyledButton
                    variant="outlined"
                    startIcon={<PeopleIcon />}
                    onClick={handleViewUsers}
                    size="small"
                  >
                    View All Users
                  </StyledButton>
                 
                </Box>
              </Paper>
              )}
            </Box>
          </TabPanel>

          {/* Settings Tab */}
          <TabPanel value={shownTab} index={3}>
            <Box sx={{ 
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
              minHeight: '100%',
              p: 2,
              borderRadius: 2
            }}>
              <Typography variant="h6" sx={{ color: 'text.primary', fontWeight: 600, mb: 3 }}>
                Company Settings
              </Typography>
              <Paper
                elevation={0}
                sx={sectionPaperSx}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <Box
                    sx={{
                      p: 0.75,
                      borderRadius: 1.5,
                      background: (theme) => alpha(theme.palette.primary.main, 0.1),
                      border: (theme) => `1px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                      mr: 1.5,
                    }}
                  >
                    <SettingsIcon sx={{ color: 'primary.main', fontSize: 16 }} />
                  </Box>
                  <Typography variant="subtitle1" sx={{ color: 'text.primary', fontWeight: 600, fontSize: '1rem' }}>
                    Company Settings
                  </Typography>
                </Box>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                  Edit company details, branding, and configuration
                </Typography>
                <StyledButton
                  variant="outlined"
                  startIcon={<EditIcon />}
                  onClick={handleEditCompanyLocal}
                >
                  Edit Company Details
                </StyledButton>
              </Paper>
            </Box>
          </TabPanel>

          {/* Wallet Tab */}
          <TabPanel value={shownTab} index={4}>
            <Box sx={{ 
              background: (theme) =>
                `linear-gradient(135deg, ${theme.palette.background.default} 0%, ${alpha(theme.palette.primary.main, 0.06)} 100%)`,
              minHeight: '100%',
              p: 0,
              borderRadius: 2,
              overflow: 'hidden'
            }}>
              <WalletManagementPanel
                company={companyData}
                onBack={undefined} // No back button since we're in a tab
              />
            </Box>
          </TabPanel>

          {/* Roles & permissions Tab (read only) */}
          {canViewRoles && (
            <TabPanel value={shownTab} index={5}>
              <Box sx={{ p: 2, borderRadius: 2 }}>
                <RolesPermissionsTab companyId={currentCompany.id} />
              </Box>
            </TabPanel>
          )}
        </Box>
      </Box>

      {/* Invite User Dialog */}
      <InviteUserDialog
        open={dialogs.showInviteDialog}
        onClose={() => closeDialog('showInviteDialog')}
        onSubmit={handleInviteUserWithRefresh}
        formData={inviteForm}
        onFormChange={setInviteForm}
        companyId={currentCompany.id}
        companyDefaultRole={companyDefaultRole(companyData)}
        companyDefaultRoleKey={companyDefaultRoleKeyOf(companyData)}
        members={companyUsers}
        isGlobalAdmin={false}
        // The join link sits in the invite dialog, so it reaches people who may invite but cannot
        // open Settings (SC-491).
        showInviteLink
      />

      {/* Edit Company Dialog */}
      <CompanyDialog
        open={dialogs.showEditCompanyDialog}
        onClose={() => closeDialog('showEditCompanyDialog')}
        onSubmit={handleCompanyDialogSubmit}
        formData={companyForm}
        onFormChange={setCompanyForm}
        mode="edit"
      />

      {/* Edit User Dialog */}
      <EditUserDialog
        open={dialogs.showEditUserDialog}
        onClose={() => closeDialog('showEditUserDialog')}
        onSubmit={handleEditUserWithRefresh}
        formData={editUserForm}
        onFormChange={setEditUserForm}
        userEmail={selectedItems.selectedUser?.user?.email}
        roleless={memberHasNoRole(selectedItems.selectedUser)}
        // The company the save goes to (handleEditUser), so the role names are that company's.
        companyId={(selectedItems.selectedCompany || currentCompany)?.id}
      />

      {/* Users Dialog */}
      <UsersDialog
        open={showUsersDialog}
        onClose={handleCloseUsersDialog}
        company={companyData as any}
        users={companyUsers}
        onEditUser={handleEditUserOpen}
        onRemoveUser={handleRemoveUser}
        onInviteUser={handleInviteUserFromUsersDialog}
        onSyncUnassignedUsers={handleSyncUnassignedUsers}
        loading={loading.loadingUsers}
        syncingUsers={syncingUsers}
        userMenuAnchor={userMenu.userMenuAnchor}
        selectedUserForMenu={userMenu.selectedUserForMenu}
        onUserMenuOpen={handleUserMenuOpen}
        onUserMenuClose={handleUserMenuClose}
        onRemoveUserFromMenu={handleRemoveUserFromMenu}
        onEditUserFromMenu={handleEditUserFromMenu}
        onRefreshUsers={() => loadCompanyUsers(currentCompany.id)}
      />

      {/* Email Template Manager Dialog */}
      <Dialog
        open={emailTemplateDialogOpen}
        onClose={handleCloseEmailTemplateDialog}
        maxWidth="xl"
        fullWidth
        PaperProps={{
          sx: {
            height: '90vh',
            maxHeight: '90vh',
            bgcolor: 'background.paper',
            color: 'text.primary',
            backgroundImage: 'none',
            backdropFilter: 'blur(10px)',
            border: (t) => `1px solid ${t.palette.divider}`,
          }
        }}
      >
        <DialogTitle sx={{ 
          bgcolor: 'action.hover',
          color: 'text.primary',
          borderBottom: 1,
          borderColor: 'divider',
        }}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <EmailIcon sx={{ color: 'primary.main' }} />
            <Typography variant="h6">
              {companyData.name} Email Templates
            </Typography>
          </Box>
        </DialogTitle>
        <DialogContent sx={{ padding: 0, overflow: 'hidden', height: 580 }}>
          <CompanyEmailTemplates companyId={companyData.id} />
        </DialogContent>
        <DialogActions sx={{ 
          bgcolor: 'action.hover',
          borderTop: 1,
          borderColor: 'divider',
          padding: '16px 24px'
        }}>
          <StyledButton 
            onClick={handleCloseEmailTemplateDialog}
            variant="outlined"
          >
            Close
          </StyledButton>
        </DialogActions>
      </Dialog>

      <CompanyRoomsDialog
        open={showRoomsDialog}
        onClose={() => setShowRoomsDialog(false)}
        company={{ id: companyData.id, name: companyData.name }}
      />
    </Box>
    </CompanyBrandedShell>
  )
} 